import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Check, ArrowRight, Sparkles } from "lucide-react";

const TIERS = [
    {
        id: "starter",
        name: "Starter",
        monthly: "$299",
        monthlyUnit: "/ month",
        included: "Up to 20 shipments / month",
        tagline: "Test the platform. Track your first shipments.",
        bestFor: "Small importers/exporters trialling visibility.",
        features: [
            "Container / MBL tracking",
            "Basic dashboard",
            "ETA updates",
            "Email alerts",
            "Up to 20 shipments / month",
            "Freight cost benchmarking · limited",
            "Email support",
        ],
        cta: { label: "Start free", to: "/#early-access" },
        highlight: false,
    },
    {
        id: "control",
        name: "Control",
        monthly: "$599",
        monthlyUnit: "/ month",
        included: "Up to 50 shipments / month",
        tagline: "Run your freight with intent — not in firefighting mode.",
        bestFor: "SME importers/exporters doing 10–50 shipments/month.",
        features: [
            "Everything in Starter",
            "PO / order tracking",
            "Supplier &amp; shipment dashboard",
            "Carrier schedule visibility",
            "Freight cost review",
            "Monthly savings report",
            "Basic invoice checking",
            "Exception alerts",
            "Priority email + WhatsApp support",
        ],
        cta: { label: "Get Control", to: "/demo" },
        highlight: true,
        badge: "Most popular",
    },
    {
        id: "premium",
        name: "Premium Advisory",
        monthly: "$999",
        monthlyUnit: "/ month",
        included: "Up to 100 shipments / month",
        tagline: "Software plus a logistics expert in your corner.",
        bestFor: "Operations leaders who want answers, not just data.",
        features: [
            "Everything in Control",
            "Freight rate benchmarking",
            "Carrier / forwarder quote comparison",
            "Monthly freight spend review",
            "Detention &amp; demurrage risk alerts",
            "Invoice audit support",
            "Savings opportunity report",
            "1-hour monthly consulting call",
            "Light tender / procurement support",
        ],
        cta: { label: "Talk to us", to: "/quote" },
        highlight: false,
    },
];

export default function Pricing() {
    return (
        <div data-testid="pricing-page" className="bg-[#0A0A0E] text-zinc-100 min-h-screen">
            <Navbar />
            <main className="pt-28 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="max-w-3xl mb-12 text-center mx-auto">
                    <span className="section-tag" data-testid="pricing-tag">Pricing · Launch</span>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-5 leading-[0.95] tracking-tighter">
                        Simple monthly plans. <br className="hidden sm:block" />
                        <span className="text-glow-cyan">No per-shipment surprises.</span>
                    </h1>
                    <p className="text-zinc-400 mt-6 leading-relaxed text-lg">
                        Three flat plans. Each includes a monthly shipment allowance and the
                        full feature set for that tier. Predictable cost, no usage meter, no
                        lock-in.
                    </p>
                </div>

                {/* Foundation Customer Offer */}
                <div
                    data-testid="foundation-offer"
                    className="card-glow rounded-2xl p-6 sm:p-7 mb-12 relative overflow-hidden"
                    style={{
                        borderColor: "rgba(0,240,255,0.35)",
                        boxShadow:
                            "inset 0 1px 0 rgba(255,255,255,0.06), 0 30px 80px -30px rgba(0,240,255,0.25)",
                    }}
                >
                    <div
                        aria-hidden
                        className="absolute -top-20 -right-20 w-72 h-72 rounded-full"
                        style={{
                            background:
                                "radial-gradient(circle, rgba(0,240,255,0.18), transparent 65%)",
                            filter: "blur(30px)",
                        }}
                    />
                    <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div className="flex items-start gap-4">
                            <div
                                className="w-11 h-11 rounded-lg flex-shrink-0 flex items-center justify-center"
                                style={{
                                    background: "rgba(255, 184, 0, 0.10)",
                                    border: "1px solid rgba(255, 184, 0, 0.35)",
                                }}
                            >
                                <Sparkles size={20} className="text-amber-400" />
                            </div>
                            <div>
                                <div className="eyebrow" style={{ color: "#FFB800" }}>
                                    Foundation Customer Offer
                                </div>
                                <h3 className="font-display text-xl sm:text-2xl text-white mt-2 tracking-tight">
                                    First 3 months at 50% off · no lock-in, cancel anytime.
                                </h3>
                                <p className="text-sm text-zinc-400 mt-2">
                                    Includes shipment visibility, freight cost review, invoice
                                    checks and a monthly savings report.
                                </p>
                            </div>
                        </div>
                        <Link to="/demo" className="btn-primary" data-testid="foundation-cta">
                            Claim foundation offer
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>

                {/* Tier cards */}
                <div className="grid lg:grid-cols-3 gap-5 mb-16">
                    {TIERS.map((t) => (
                        <article
                            key={t.id}
                            data-testid={`pricing-tier-${t.id}`}
                            className={`card-glow rounded-2xl p-7 lg:p-8 flex flex-col relative ${
                                t.highlight ? "border-cyan-400/50" : ""
                            }`}
                            style={
                                t.highlight
                                    ? {
                                          boxShadow:
                                              "inset 0 1px 0 rgba(255,255,255,0.08), 0 30px 80px -30px rgba(0,240,255,0.30)",
                                      }
                                    : {}
                            }
                        >
                            {t.highlight && t.badge && (
                                <span className="absolute top-5 right-5 text-[10px] font-mono-accent uppercase tracking-[0.2em] text-cyan-300 px-2 py-1 rounded-full border border-cyan-400/40 bg-cyan-400/10">
                                    {t.badge}
                                </span>
                            )}
                            <div className="eyebrow">{t.name}</div>

                            {/* Price block */}
                            <div className="mt-5">
                                <div className="flex items-baseline gap-2">
                                    <span className="font-display text-5xl text-white tracking-tighter">
                                        {t.monthly}
                                    </span>
                                    <span className="text-zinc-500 text-sm">{t.monthlyUnit}</span>
                                </div>
                                <div className="mt-2 text-sm text-zinc-400">
                                    <span className="text-cyan-300 font-medium">{t.included}</span>
                                </div>
                            </div>

                            <p className="font-display text-lg text-white mt-6 tracking-tight">
                                {t.tagline}
                            </p>
                            <p className="text-xs font-mono-accent text-zinc-500 mt-2 uppercase tracking-[0.16em]">
                                Best for · {t.bestFor}
                            </p>

                            <ul className="space-y-3 mt-6 flex-1">
                                {t.features.map((f, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-3 text-sm text-zinc-300"
                                        dangerouslySetInnerHTML={{
                                            __html: `<span class="mt-0.5 flex-shrink-0 text-cyan-400">✓</span> <span>${f}</span>`,
                                        }}
                                    />
                                ))}
                            </ul>

                            <Link
                                to={t.cta.to}
                                className={`${t.highlight ? "btn-primary" : "btn-secondary"} justify-center mt-7`}
                                data-testid={`pricing-cta-${t.id}`}
                            >
                                {t.cta.label}
                                <ArrowRight size={16} />
                            </Link>
                        </article>
                    ))}
                </div>

                {/* Enterprise teaser */}
                <section
                    data-testid="pricing-enterprise"
                    className="card-glow rounded-2xl p-8 sm:p-10 grid lg:grid-cols-12 gap-8 items-start mb-10"
                >
                    <div className="lg:col-span-7">
                        <div className="eyebrow">Enterprise · Managed Procurement</div>
                        <h2 className="font-display text-2xl sm:text-3xl text-white mt-3 tracking-tight">
                            For shippers running tenders, 100+ shipments/month, or annual
                            freight contracts.
                        </h2>
                        <p className="text-zinc-400 mt-4 leading-relaxed">
                            Custom pricing from{" "}
                            <span className="text-white font-medium">$1,500–$5,000 / month</span>{" "}
                            depending on scope. Optionally combined with a{" "}
                            <span className="text-cyan-300 font-medium">success-fee model</span>{" "}
                            — typically <span className="text-white">10–20%</span> of verified
                            freight savings, capped and agreed in advance.
                        </p>
                        <ul className="grid sm:grid-cols-2 gap-2 mt-6">
                            {[
                                "Freight tender management",
                                "Carrier &amp; forwarder negotiation",
                                "Contract review",
                                "Monthly board-style report",
                                "Verified savings tracking",
                                "Quarterly business review",
                                "API / integration support",
                                "Dedicated account team",
                            ].map((f, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-2 text-sm text-zinc-300"
                                    data-testid={`enterprise-feature-${i}`}
                                >
                                    <Check size={14} className="text-cyan-400 mt-1 flex-shrink-0" />
                                    <span dangerouslySetInnerHTML={{ __html: f }} />
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-5">
                        <div
                            className="rounded-xl p-6 border border-white/10"
                            style={{ background: "rgba(0,240,255,0.04)" }}
                        >
                            <div className="eyebrow">Example · success fee</div>
                            <p className="mt-3 text-zinc-300 text-sm leading-relaxed">
                                If WE DELVR LOGISTICS identifies and helps you recover{" "}
                                <span className="text-white font-medium">$50,000</span> of
                                annual freight cost, a <span className="text-cyan-300">10% success fee</span>{" "}
                                = <span className="text-white font-display text-xl">$5,000</span>.
                            </p>
                            <p className="mt-4 text-xs font-mono-accent text-zinc-500 uppercase tracking-[0.16em]">
                                You only pay on verified savings.
                            </p>
                            <Link
                                to="/quote"
                                className="btn-primary mt-6 justify-center w-full"
                                data-testid="enterprise-cta"
                            >
                                Scope an engagement
                                <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Footnote */}
                <div className="text-center space-y-2">
                    <p className="text-[11px] font-mono-accent text-zinc-500 uppercase tracking-[0.18em]">
                        All prices in AUD · GST extra · no setup fee · no lock-in contracts
                    </p>
                    <p className="text-[11px] font-mono-accent text-zinc-600 uppercase tracking-[0.18em]">
                        Launch pricing held for 12 months for foundation customers
                    </p>
                </div>
            </main>
            <Footer />
        </div>
    );
}
